"use client"
import Link from "next/link"
import Image from "next/image"
import SellerLink from "@/components/SellerLink"
import { useEffect, useState, useMemo } from "react"
import { useParams } from "next/navigation"

// ─── Types ────────────────────────────────────────────────────────────────────
type UnitStatus = "AVAILABLE" | "RESERVED" | "SOLD"
type Unit = {
  id: string; unitNumber: string; type: string
  beds: number; baths: number; floorArea?: number
  price: number; status: UnitStatus
}
type Update = {
  id: string; title: string; body: string
  photoUrls: string[]; phase?: string; createdAt: string
}
type Developer = { id: string; name?: string; companyName?: string; phone?: string; kycSubmission?: { status: string } | null }
type Project = {
  id: string; name: string; suburb: string; city: string
  description?: string; totalUnits: number; sold: number; reserved: number
  completionDate?: string; cpcaRef?: string; plansUrl?: string
  coverImage?: string; trustScore: number; projectType?: string; galleryImages?: string[]
  permitProvided?: boolean; permitVerified?: boolean
  parentDeedProvided?: boolean; parentDeedVerified?: boolean
  complianceCertProvided?: boolean; complianceCertVerified?: boolean
  units: Unit[]; updates: Update[]; developer?: Developer
  /** Reachability without the number itself — the API withholds phone from anonymous callers. */
  developerHasPhone?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PHASES = ["Site Prep", "Foundation", "Structure", "Finishing", "Handover"]

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getProjectStatus(p: Project) {
  const available = p.totalUnits - p.sold - p.reserved
  if (p.sold >= p.totalUnits && p.totalUnits > 0)
    return { label: "SOLD OUT", bg: "rgba(196,68,68,0.18)", color: "#c44444", border: "rgba(196,68,68,0.35)" }
  if (p.totalUnits > 0 && available / p.totalUnits < 0.2 && available > 0)
    return { label: "SELLING FAST", bg: "rgba(224,123,57,0.18)", color: "#e07b39", border: "rgba(224,123,57,0.35)" }
  if (p.sold === 0 && p.totalUnits > 0)
    return { label: "NOW SELLING", bg: "rgba(61,139,95,0.18)", color: "#3d8b5f", border: "rgba(61,139,95,0.35)" }
  return { label: "FOR SALE", bg: "rgba(61,139,95,0.15)", color: "#3d8b5f", border: "rgba(61,139,95,0.3)" }
}

function groupByType(units: Unit[]): Record<string, Unit[]> {
  return units.reduce((acc, u) => {
    if (!acc[u.type]) acc[u.type] = []
    acc[u.type].push(u)
    return acc
  }, {} as Record<string, Unit[]>)
}

const fmt = (n: number) => `$${n.toLocaleString()}`
const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })

// ─── Component ────────────────────────────────────────────────────────────────
export default function DeveloperProjectPage() {
  const params = useParams()
  const id = params.id as string

  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [similar, setSimilar] = useState<any[]>([])
  const [activeType, setActiveType] = useState<string | null>(null)
  const [showFullTable, setShowFullTable] = useState(false)
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)
  const [depositPct, setDepositPct] = useState(25)
  const [calcMonths, setCalcMonths] = useState(24)
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "", unitType: "" })
  const [waitlistLoading, setWaitlistLoading] = useState(false)
  const [waitlistDone, setWaitlistDone] = useState(false)
  const [waitlistError, setWaitlistError] = useState("")
  const [subEmail, setSubEmail] = useState("")
  const [subName, setSubName] = useState("")
  const [subLoading, setSubLoading] = useState(false)
  const [subDone, setSubDone] = useState(false)
  const [subError, setSubError] = useState("")
  const [reservingId, setReservingId] = useState<string | null>(null)
  const [reserveMsg, setReserveMsg] = useState<{ id: string; type: "success" | "error"; text: string } | null>(null)
  const [shareMsg, setShareMsg] = useState("")

  useEffect(() => {
    fetch("/api/developers/" + id)
      .then(r => r.json())
      .then(d => { setProject(d.data ?? null); setLoading(false) })
      .catch(() => setLoading(false))
    fetch("/api/developers")
      .then(r => r.json())
      .then(d => setSimilar((d.data ?? []).filter((p: any) => p.id !== id).slice(0, 3)))
      .catch(() => {})
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived ──────────────────────────────────────────────────────────────
  const d = useMemo(() => {
    if (!project) return null
    const available = project.totalUnits - project.sold - project.reserved
    const pctSold = project.totalUnits > 0 ? Math.round((project.sold / project.totalUnits) * 100) : 0
    const prices = project.units.map(u => u.price)
    const minPrice = prices.length ? Math.min(...prices) : null
    const unitsByType = groupByType(project.units)
    const typeList = Object.keys(unitsByType)
    const allPhotos = Array.from(new Set([
      project.coverImage,
      ...(project.galleryImages ?? []),
      ...project.updates.flatMap(u => u.photoUrls),
    ].filter(Boolean) as string[]))
    const currentPhase = project.updates.find(u => u.phase)?.phase ?? null
    const currentPhaseIdx = currentPhase
      ? PHASES.findIndex(p => p.toLowerCase().startsWith(currentPhase.toLowerCase().split(" ")[0]))
      : -1
    const filteredUnits = activeType ? project.units.filter(u => u.type === activeType) : project.units
    // The API no longer hands the developer's number to anonymous visitors, and
    // the pre-filled message is composed server-side from the project row — a
    // caller-supplied text parameter would be a phishing primitive.
    const whatsappNum = project.developerHasPhone ? project.id : null
    const whatsappEnq = "enquiry"
    const whatsappPlan = "plan"
    const kycVerified = project.developer?.kycSubmission?.status === "APPROVED"
    const isLand = project.projectType === "LAND"
    return { available, pctSold, minPrice, unitsByType, typeList, allPhotos, currentPhase, currentPhaseIdx, filteredUnits, whatsappNum, whatsappEnq, whatsappPlan, kycVerified, isLand }
  }, [project, activeType])

  // Arrow keys / Escape in the lightbox. Registered unconditionally and no-ops
  // while it is closed. It MUST stay above the `if (loading) return` below: it
  // used to sit under those early returns (despite a comment saying it must
  // not), so the first render had one hook fewer than the render after the
  // project loaded, and React error #310 crashed every development page.
  useEffect(() => {
    if (lightboxIdx === null) return
    const total = d?.allPhotos.length ?? 0
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setLightboxIdx(null)
      else if (e.key === "ArrowRight" && total > 1) setLightboxIdx(i => i === null ? null : (i + 1) % total)
      else if (e.key === "ArrowLeft" && total > 1) setLightboxIdx(i => i === null ? null : (i - 1 + total) % total)
    }
    window.addEventListener("keydown", onKey)
    // Stop the page scrolling behind the overlay.
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev }
  }, [lightboxIdx, d?.allPhotos.length])

  // ─── Handlers ─────────────────────────────────────────────────────────────
  async function reserveUnit(unitId: string) {
    setReservingId(unitId); setReserveMsg(null)
    try {
      const res = await fetch(`/api/units/${unitId}/reserve`, { method: "POST" })
      const data = await res.json()
      if (res.ok) {
        setReserveMsg({ id: unitId, type: "success", text: "Reserved! Check your email." })
        fetch("/api/developers/" + id).then(r => r.json()).then(x => setProject(x.data ?? null))
      } else {
        setReserveMsg({ id: unitId, type: "error", text: data.error ?? "Could not reserve." })
      }
    } catch {
      setReserveMsg({ id: unitId, type: "error", text: "Something went wrong." })
    }
    setReservingId(null)
  }

  async function joinWaitlist() {
    if (!waitlistForm.name || !waitlistForm.email) { setWaitlistError("Name and email are required"); return }
    setWaitlistLoading(true); setWaitlistError("")
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: id, ...waitlistForm }),
      })
      const data = await res.json()
      if (data.success) setWaitlistDone(true)
      else setWaitlistError(data.error ?? "Something went wrong")
    } catch { setWaitlistError("Something went wrong") }
    setWaitlistLoading(false)
  }

  async function subscribeToUpdates() {
    if (!subEmail) { setSubError("Email is required"); return }
    setSubLoading(true); setSubError("")
    try {
      const res = await fetch(`/api/developers/${id}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: subEmail, name: subName || undefined }),
      })
      const data = await res.json()
      if (data.success) setSubDone(true)
      else setSubError(data.error ?? "Something went wrong")
    } catch { setSubError("Something went wrong") }
    setSubLoading(false)
  }

  function handleShare() {
    const url = window.location.href
    if (navigator.share) {
      navigator.share({ title: project?.name, url }).catch(() => {})
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setShareMsg("Link copied!")
        setTimeout(() => setShareMsg(""), 2500)
      })
    }
  }

  // ─── Calc values ──────────────────────────────────────────────────────────
  const calcPrice = d?.minPrice ?? 50000
  const depositAmt = Math.round(calcPrice * depositPct / 100)
  const monthly = Math.round((calcPrice - depositAmt) / calcMonths)

  // ─── Shared styles ────────────────────────────────────────────────────────
  const inp: React.CSSProperties = {
    width: "100%", padding: "11px 13px", border: "1px solid var(--line)",
    borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none",
  }

  // ─── Loading / Not found ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", display: "grid", placeItems: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 40, height: 40, border: "3px solid var(--line)", borderTopColor: "var(--gold)", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
        <div style={{ color: "var(--muted)", fontSize: 14 }}>Loading project…</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
  if (!project || !d) return (
    <div style={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🏗️</div>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, color: "var(--navy)", marginBottom: 12 }}>Project not found</div>
        <Link href="/developers" style={{ color: "var(--gold-deep)", textDecoration: "none", fontWeight: 600 }}>← Back to all projects</Link>
      </div>
    </div>
  )

  const status = getProjectStatus(project)

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>

      {/* ═══ LIGHTBOX ══════════════════════════════════════════════════════ */}
      {lightboxIdx !== null && d.allPhotos[lightboxIdx] && (
        <div onClick={() => setLightboxIdx(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
          <img src={d.allPhotos[lightboxIdx]} alt={`${project.name} photo ${lightboxIdx + 1}`}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "92vw", maxHeight: "84vh", objectFit: "contain", borderRadius: 10, cursor: "default" }} />

          {d.allPhotos.length > 1 && (
            <>
              <button aria-label="Previous photo"
                onClick={e => { e.stopPropagation(); setLightboxIdx(i => i === null ? null : (i - 1 + d.allPhotos.length) % d.allPhotos.length) }}
                style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "white", width: 46, height: 46, borderRadius: "50%", fontSize: 20, cursor: "pointer", display: "grid", placeItems: "center" }}>‹</button>
              <button aria-label="Next photo"
                onClick={e => { e.stopPropagation(); setLightboxIdx(i => i === null ? null : (i + 1) % d.allPhotos.length) }}
                style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "white", width: 46, height: 46, borderRadius: "50%", fontSize: 20, cursor: "pointer", display: "grid", placeItems: "center" }}>›</button>
              <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.75)", fontSize: 13, fontWeight: 600, background: "rgba(0,0,0,0.4)", padding: "6px 14px", borderRadius: 20 }}>
                {lightboxIdx + 1} of {d.allPhotos.length}
              </div>
            </>
          )}
          <button aria-label="Close" onClick={e => { e.stopPropagation(); setLightboxIdx(null) }}
            style={{ position: "absolute", top: 20, right: 28, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", width: 40, height: 40, borderRadius: "50%", fontSize: 18, cursor: "pointer", display: "grid", placeItems: "center" }}>✕</button>
        </div>
      )}

      {/* ═══ HERO ══════════════════════════════════════════════════════════ */}
      <div style={{ position: "relative", height: "min(60vh, 520px)", overflow: "hidden", background: "var(--navy)" }}>
        {project.coverImage && (
          // next/image, not a raw <img>: every image on this page was served at
          // full upload resolution regardless of the box it was painted into.
          // On a market where mobile data is bought by the bundle — and where
          // this app ships an explicit Data Saver mode — that is a real cost.
          <Image src={project.coverImage} alt={project.name} fill priority
            sizes="100vw"
            style={{ objectFit: "cover", objectPosition: "center" }} />
        )}
        {/* Gradient overlay */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, rgba(14,22,42,0.25) 0%, rgba(14,22,42,0.65) 50%, rgba(14,22,42,0.96) 100%)" }} />

        {/* Top-right action buttons */}
        <div style={{ position: "absolute", top: 20, right: 24, display: "flex", gap: 8, zIndex: 10 }}>
          <button onClick={handleShare}
            style={{ padding: "8px 16px", background: "rgba(14,22,42,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(253,251,247,0.2)", borderRadius: 8, color: "var(--cream)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s" }}>
            {shareMsg || "⤴ Share"}
          </button>
          {d.whatsappNum && (
            <a href={`/api/go/whatsapp?project=${encodeURIComponent(project.id)}&topic=${d.whatsappEnq}`} target="_blank" rel="noopener noreferrer"
              style={{ padding: "8px 16px", background: "#075E54", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.025.507 3.934 1.395 5.609L0 24l6.545-1.371A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.001-1.366l-.36-.213-3.882.814.826-3.799-.234-.38A9.818 9.818 0 1112 21.818z"/></svg>
              WhatsApp
            </a>
          )}
        </div>

        {/* Hero content */}
        <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "0 24px 44px", maxWidth: 1200, margin: "0 auto", boxSizing: "border-box", width: "100%" }}>
          <Link href="/developers" style={{ color: "rgba(253,251,247,0.5)", fontSize: 13, textDecoration: "none", marginBottom: 18, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
            ← All projects
          </Link>

          {/* Badge row */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", background: status.bg, color: status.color, border: `1px solid ${status.border}` }}>
              {status.label}
            </span>
            {d.kycVerified && (
              <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(200,150,62,0.2)", color: "var(--gold)", border: "1px solid rgba(200,150,62,0.4)", display: "flex", alignItems: "center", gap: 5 }}>
                ✓ KYC Verified Developer
              </span>
            )}
            {d.minPrice && (
              <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(253,251,247,0.12)", color: "rgba(253,251,247,0.9)", border: "1px solid rgba(253,251,247,0.2)" }}>
                From {fmt(d.minPrice)}
              </span>
            )}
          </div>

          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(28px, 5vw, 54px)", fontWeight: 700, color: "var(--cream)", lineHeight: 1.1, marginBottom: 10 }}>
            {project.name}
          </h1>
          <div style={{ fontSize: 15, color: "rgba(253,251,247,0.65)", marginBottom: 28, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>📍</span>
            <span>{project.suburb}, {project.city}</span>
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 24 }}>
            {[
              { label: "Total units", value: project.totalUnits, color: "var(--gold)" },
              { label: "Available", value: d.available, color: d.available > 0 ? "#6fcf97" : "#f87171" },
              { label: "Reserved", value: project.reserved, color: "rgba(253,251,247,0.7)" },
              { label: "Sold", value: project.sold, color: "rgba(253,251,247,0.45)" },
              ...(project.completionDate ? [{
                label: "Est. completion",
                value: new Date(project.completionDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
                color: "rgba(253,251,247,0.75)",
              }] : []),
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "rgba(253,251,247,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* % sold progress bar */}
          {project.totalUnits > 0 && (
            <div style={{ maxWidth: 460 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                <span style={{ fontSize: 12, color: "rgba(253,251,247,0.55)" }}>{d.available} units remaining</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: d.pctSold > 80 ? "#f87171" : d.pctSold > 50 ? "var(--gold)" : "#6fcf97" }}>{d.pctSold}% sold</span>
              </div>
              <div style={{ height: 5, background: "rgba(253,251,247,0.12)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${d.pctSold}%`, borderRadius: 3, transition: "width 0.8s ease",
                  background: d.pctSold > 80 ? "#f87171" : d.pctSold > 50 ? "var(--gold)" : "#6fcf97" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ PHOTO STRIP (from construction updates) ══════════════════════ */}
      {d.allPhotos.length > 0 && (
        <div style={{ background: "#0e162a", borderBottom: "1px solid rgba(253,251,247,0.07)", padding: "14px 24px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
              {d.allPhotos.slice(0, 14).map((src, i) => (
                <Image key={i} src={src} alt={`${project.name} photo ${i + 1}`} width={112} height={72} onClick={() => setLightboxIdx(i)}
                  style={{ width: 112, height: 72, objectFit: "cover", borderRadius: 7, flexShrink: 0, cursor: "zoom-in", opacity: 0.82, transition: "opacity 0.15s, transform 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1.03)" }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "0.82"; e.currentTarget.style.transform = "scale(1)" }}
                />
              ))}
              {d.allPhotos.length > 14 && (
                <div onClick={() => setLightboxIdx(14)}
                  style={{ width: 112, height: 72, borderRadius: 7, flexShrink: 0, background: "rgba(253,251,247,0.1)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "rgba(253,251,247,0.6)" }}>
                  +{d.allPhotos.length - 14}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MAIN GRID ═════════════════════════════════════════════════════ */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .dev-main-grid { display: grid; grid-template-columns: 1fr 360px; gap: 52px; align-items: start; }
        .dev-sidebar-col { position: sticky; top: 88px; }
        @media (max-width: 960px) {
          .dev-main-grid { grid-template-columns: 1fr !important; gap: 32px; }
          .dev-sidebar-col { position: static !important; top: unset !important; }
        }
        .unit-cell:hover { transform: scale(1.18) !important; box-shadow: 0 3px 10px rgba(0,0,0,0.25) !important; z-index: 1; }
        .sim-card:hover { box-shadow: 0 8px 32px rgba(27,45,84,0.14) !important; transform: translateY(-2px); }
        .sim-card { transition: box-shadow 0.18s, transform 0.18s; }
        .tab-pill { transition: background 0.15s, color 0.15s; }
        .type-card { transition: background 0.15s, border-color 0.15s, transform 0.12s; }
        .type-card:hover { transform: translateY(-1px); }
      `}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "52px 24px" }}>
        <div className="dev-main-grid">

          {/* ────────────────── LEFT COLUMN ───────────────────────────── */}
          <div>

            {/* About */}
            {project.description && (
              <section style={{ marginBottom: 52 }}>
                <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 16 }}>About this development</h2>
                <p style={{ fontSize: 15, color: "var(--ink)", lineHeight: 1.8, whiteSpace: "pre-line" }}>{project.description}</p>
                {project.plansUrl && (
                  <a href={project.plansUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 18, padding: "9px 18px", background: "rgba(27,45,84,0.05)", border: "1px solid var(--line)", borderRadius: 9, fontSize: 13, color: "var(--navy)", textDecoration: "none", fontWeight: 600 }}>
                    📄 View site plan / brochure
                  </a>
                )}
              </section>
            )}

            {/* Units & availability */}
            <section style={{ marginBottom: 52 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 22, flexWrap: "wrap", gap: 10 }}>
                <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)" }}>{d.isLand ? "Stands & availability" : "Units & availability"}</h2>
                {d.typeList.length > 1 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button className="tab-pill" onClick={() => setActiveType(null)}
                      style={{ padding: "5px 14px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: activeType === null ? "var(--navy)" : "var(--cream)", color: activeType === null ? "var(--cream)" : "var(--navy)" }}>
                      All
                    </button>
                    {d.typeList.map(tn => (
                      <button key={tn} className="tab-pill" onClick={() => setActiveType(a => a === tn ? null : tn)}
                        style={{ padding: "5px 14px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: activeType === tn ? "var(--navy)" : "var(--cream)", color: activeType === tn ? "var(--cream)" : "var(--navy)" }}>
                        {tn}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Unit type summary cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14, marginBottom: 30 }}>
                {Object.entries(d.unitsByType)
                  .filter(([tn]) => activeType === null || tn === activeType)
                  .map(([tn, typeUnits]) => {
                    const avail = typeUnits.filter(u => u.status === "AVAILABLE").length
                    const resv = typeUnits.filter(u => u.status === "RESERVED").length
                    const sold = typeUnits.length - avail - resv
                    const prices = typeUnits.map(u => u.price)
                    const lo = Math.min(...prices), hi = Math.max(...prices)
                    const sample = typeUnits[0]
                    const isActive = activeType === tn
                    return (
                      <div key={tn} className="type-card" onClick={() => setActiveType(a => a === tn ? null : tn)}
                        style={{ background: isActive ? "var(--navy)" : "white", border: `1.5px solid ${isActive ? "var(--navy)" : "var(--line)"}`, borderRadius: 14, padding: "18px 16px", cursor: "pointer", boxShadow: isActive ? "0 4px 20px rgba(27,45,84,0.18)" : "0 1px 4px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 600, color: isActive ? "var(--cream)" : "var(--navy)", marginBottom: 5 }}>{tn}</div>
                        <div style={{ fontSize: 12, color: isActive ? "rgba(253,251,247,0.55)" : "var(--muted)", marginBottom: 12 }}>
                          {d.isLand
                            ? (sample.floorArea ? `${sample.floorArea} m²` : tn)
                            : `${sample.beds}bd · ${sample.baths}ba${sample.floorArea ? ` · ${sample.floorArea}m²` : ""}`}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: isActive ? "var(--gold)" : "var(--navy)", marginBottom: 10 }}>
                          {lo === hi ? fmt(lo) : `${fmt(lo)} – ${fmt(hi)}`}
                        </div>
                        {/* Availability bar */}
                        <div style={{ display: "flex", height: 4, borderRadius: 2, overflow: "hidden", gap: 1, marginBottom: 7 }}>
                          {avail > 0 && <span style={{ flex: avail, background: "#4caf73" }} />}
                          {resv > 0 && <span style={{ flex: resv, background: "#c8963e" }} />}
                          {sold > 0 && <span style={{ flex: sold, background: "rgba(196,68,68,0.45)" }} />}
                        </div>
                        <div style={{ fontSize: 11, color: isActive ? "rgba(253,251,247,0.45)" : "var(--muted)" }}>{avail} of {typeUnits.length} available</div>
                      </div>
                    )
                  })}
              </div>

              {/* Visual unit availability grid (only if ≤ 200 units) */}
              {project.units.length > 0 && project.units.length <= 200 && (
                <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, marginBottom: 22 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Unit availability map</div>
                    <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--muted)" }}>
                      {[["#4caf73", "Available"], ["#c8963e", "Reserved"], ["rgba(196,68,68,0.55)", "Sold"]].map(([bg, lbl]) => (
                        <span key={lbl as string} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: bg as string, display: "inline-block", flexShrink: 0 }} />
                          {lbl}
                        </span>
                      ))}
                    </div>
                  </div>
                  {Object.entries(d.unitsByType)
                    .filter(([tn]) => activeType === null || tn === activeType)
                    .map(([tn, typeUnits]) => (
                      <div key={tn} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 9 }}>{tn}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {typeUnits.map(u => (
                            <div key={u.id} className={u.status === "AVAILABLE" ? "unit-cell" : ""}
                              title={`Unit ${u.unitNumber} · ${fmt(u.price)}${u.floorArea ? ` · ${u.floorArea}m²` : ""} · ${u.status}`}
                              onClick={() => u.status === "AVAILABLE" && reserveUnit(u.id)}
                              style={{
                                width: 34, height: 34, borderRadius: 6,
                                cursor: u.status === "AVAILABLE" ? "pointer" : "default",
                                background: u.status === "AVAILABLE" ? "#3d8b5f" : u.status === "RESERVED" ? "#c8963e" : "rgba(196,68,68,0.45)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 7, fontWeight: 800, color: "rgba(255,255,255,0.9)",
                                border: u.status === "AVAILABLE" ? "1.5px solid rgba(61,139,95,0.5)" : "1.5px solid transparent",
                                position: "relative", transition: "transform 0.1s, box-shadow 0.1s",
                                flexShrink: 0,
                              }}>
                              {u.unitNumber.length <= 5 ? u.unitNumber : ""}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  {d.filteredUnits.filter(u => u.status === "AVAILABLE").length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Click a green unit to reserve it instantly</div>
                  )}
                </div>
              )}

              {/* Full unit table (collapsible) */}
              <button onClick={() => setShowFullTable(v => !v)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--gold-deep)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "6px 0", marginBottom: showFullTable ? 16 : 0 }}>
                {showFullTable ? "▲ Hide" : "▼ Show"} full {d.isLand ? "stand" : "unit"} breakdown ({d.filteredUnits.length} {d.isLand ? "stands" : "units"})
              </button>
              {showFullTable && (
                <div style={{ border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden", marginTop: 4 }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                      <thead>
                        <tr style={{ background: "var(--cream-warm)", borderBottom: "1px solid var(--line)" }}>
                          {(d.isLand
                            ? ["Stand No.", "Zoning", "Size (m²)", "Price", "Price/m²", "Status", ""]
                            : ["Unit", "Type", "Bed / Bath", "Area", "Price", "Price/m²", "Status", ""]
                          ).map(h => (
                            <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {d.filteredUnits.map((u, i) => (
                          <tr key={u.id} style={{ borderBottom: "1px solid var(--line)", background: i % 2 === 0 ? "white" : "var(--cream)" }}>
                            <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--navy)", whiteSpace: "nowrap" }}>{u.unitNumber}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink)" }}>{u.type}</td>
                            {!d.isLand && <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink)", whiteSpace: "nowrap" }}>{u.beds}bd / {u.baths}ba</td>}
                            <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink)", whiteSpace: "nowrap" }}>{u.floorArea ? `${u.floorArea}m²` : "–"}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--navy)", whiteSpace: "nowrap" }}>{fmt(u.price)}</td>
                            <td style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "var(--gold-deep)", whiteSpace: "nowrap" }}>{u.floorArea ? `$${Math.round(u.price / u.floorArea).toLocaleString()}/m²` : "–"}</td>
                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                                background: u.status === "AVAILABLE" ? "rgba(61,139,95,0.1)" : u.status === "RESERVED" ? "rgba(200,150,62,0.1)" : "rgba(196,68,68,0.08)",
                                color: u.status === "AVAILABLE" ? "#3d8b5f" : u.status === "RESERVED" ? "var(--gold-deep)" : "var(--red)" }}>
                                {u.status}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              {u.status === "AVAILABLE" && (
                                <div>
                                  <button onClick={() => reserveUnit(u.id)} disabled={reservingId === u.id}
                                    style={{ padding: "5px 14px", borderRadius: 6, background: reservingId === u.id ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", fontSize: 11, fontWeight: 700, cursor: reservingId === u.id ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                    {reservingId === u.id ? "…" : "Reserve"}
                                  </button>
                                  {reserveMsg?.id === u.id && (
                                    <div style={{ fontSize: 11, marginTop: 4, color: reserveMsg.type === "success" ? "#3d8b5f" : "var(--red)", maxWidth: 140 }}>{reserveMsg.text}</div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>

            {/* Construction progress */}
            <section style={{ marginBottom: 52 }}>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 28 }}>Construction progress</h2>

              {/* Phase stepper */}
              <div style={{ marginBottom: 36 }}>
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                  {PHASES.map((phase, i) => {
                    const done = i < d.currentPhaseIdx
                    const current = i === d.currentPhaseIdx
                    const isLast = i === PHASES.length - 1
                    return (
                      <div key={phase} style={{ display: "flex", alignItems: "center", flex: isLast ? 0 : 1, minWidth: 0 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center",
                            fontSize: done ? 14 : 13, fontWeight: 700, flexShrink: 0,
                            background: done ? "#4caf73" : current ? "var(--gold)" : "var(--cream-warm)",
                            border: `2px solid ${done ? "#4caf73" : current ? "var(--gold)" : "var(--line)"}`,
                            color: done || current ? "white" : "var(--muted)",
                            boxShadow: current ? "0 0 0 5px rgba(200,150,62,0.18)" : "none",
                          }}>
                            {done ? "✓" : i + 1}
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap", textAlign: "center",
                            color: current ? "var(--gold-deep)" : done ? "#3d8b5f" : "var(--muted)" }}>
                            {phase}
                          </div>
                        </div>
                        {!isLast && (
                          <div style={{ flex: 1, height: 2, margin: "0 4px", marginBottom: 26, background: done ? "#4caf73" : "var(--line)" }} />
                        )}
                      </div>
                    )
                  })}
                </div>
                {d.currentPhase && (
                  <div style={{ marginTop: 16, fontSize: 13, color: "var(--muted)" }}>
                    Currently in: <strong style={{ color: "var(--gold-deep)" }}>{d.currentPhase}</strong>
                  </div>
                )}
                {!d.currentPhase && (
                  <div style={{ marginTop: 16, fontSize: 13, color: "var(--muted)" }}>
                    Construction updates will show progress here
                  </div>
                )}
              </div>

              {/* Update feed */}
              {project.updates.length === 0 ? (
                <div style={{ padding: "36px 28px", textAlign: "center", color: "var(--muted)", fontSize: 14, background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16 }}>
                  Updates will appear here as the project progresses.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {project.updates.map(u => (
                    <div key={u.id} style={{ background: "white", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                      <div style={{ padding: "22px 24px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
                          <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)" }}>{u.title}</div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                            {u.phase && (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: "rgba(200,150,62,0.1)", color: "var(--gold-deep)" }}>{u.phase}</span>
                            )}
                            <span style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>{fmtDate(u.createdAt)}</span>
                          </div>
                        </div>
                        <p style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.7, margin: 0 }}>{u.body}</p>
                      </div>
                      {u.photoUrls.length > 0 && (
                        <div style={{ display: "grid", gap: 2, gridTemplateColumns: u.photoUrls.length === 1 ? "1fr" : u.photoUrls.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr" }}>
                          {u.photoUrls.slice(0, 3).map((src, i) => (
                            <Image key={i} src={src} alt={`${project.name} construction photo`} width={400} height={180} onClick={() => { const at = d.allPhotos.indexOf(src); if (at >= 0) setLightboxIdx(at) }}
                              style={{ width: "100%", height: 180, objectFit: "cover", cursor: "zoom-in", display: "block" }} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Similar developments */}
            {similar.length > 0 && (
              <section style={{ marginBottom: 20 }}>
                <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--navy)", marginBottom: 18 }}>Other developments</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
                  {similar.map(p => (
                    <Link key={p.id} href={`/developers/${p.id}`} className="sim-card"
                      style={{ textDecoration: "none", display: "block", background: "white", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
                      {p.coverImage ? (
                        <Image src={p.coverImage} alt={p.name} width={280} height={120} sizes="(max-width: 640px) 100vw, 280px" style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
                      ) : (
                        <div style={{ width: "100%", height: 120, background: "var(--navy)", display: "grid", placeItems: "center" }}>
                          <span style={{ fontSize: 36 }}>🏗️</span>
                        </div>
                      )}
                      <div style={{ padding: 16 }}>
                        <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>{p.suburb}, {p.city}</div>
                        {p.minPrice && <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gold-deep)" }}>From {fmt(p.minPrice)}</div>}
                        {p.availableUnits > 0 && <div style={{ fontSize: 12, color: "#3d8b5f", marginTop: 3 }}>{p.availableUnits} units available</div>}
                      </div>
                    </Link>
                  ))}
                </div>
                <div style={{ marginTop: 16 }}>
                  <Link href="/developers" style={{ fontSize: 13, fontWeight: 600, color: "var(--gold-deep)", textDecoration: "none" }}>View all developments →</Link>
                </div>
              </section>
            )}
          </div>

          {/* ────────────────── RIGHT SIDEBAR ─────────────────────────── */}
          <div className="dev-sidebar-col" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Developer trust card */}
            <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: 18, padding: 24, boxShadow: "0 4px 24px rgba(27,45,84,0.09)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, paddingBottom: 18, borderBottom: "1px solid var(--line)", marginBottom: 18 }}>
                {(() => {
                  const devName = project.developer?.companyName || project.developer?.name || "Developer"
                  return <>
                    <div style={{ width: 50, height: 50, borderRadius: "50%", background: "var(--navy)", display: "grid", placeItems: "center", fontSize: 20, fontWeight: 700, color: "var(--gold)", flexShrink: 0 }}>
                      {devName[0].toUpperCase()}
                    </div>
                    <div>
                      <SellerLink agent={project.developer} style={{ fontWeight: 700, fontSize: 15, color: "var(--navy)", textDecoration: "none", display: "block" }}>{devName}</SellerLink>
                  {d.kycVerified ? (
                    <div style={{ fontSize: 12, color: "#3d8b5f", fontWeight: 700, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ display: "inline-grid", placeItems: "center", width: 16, height: 16, borderRadius: "50%", background: "#3d8b5f", color: "white", fontSize: 9 }}>✓</span>
                      Verified on Zim Estates
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Developer</div>
                  )}
                    </div>
                  </>
                })()}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {project.developer?.phone && (
                  <a href={`tel:${project.developer.phone}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 10, textDecoration: "none", color: "var(--navy)", fontSize: 14, fontWeight: 600 }}>
                    <span>📞</span> {project.developer.phone}
                  </a>
                )}
                {d.whatsappNum && (
                  <a href={`/api/go/whatsapp?project=${encodeURIComponent(project.id)}&topic=${d.whatsappEnq}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 15px", background: "#075E54", borderRadius: 10, textDecoration: "none", color: "white", fontSize: 14, fontWeight: 700 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.025.507 3.934 1.395 5.609L0 24l6.545-1.371A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.001-1.366l-.36-.213-3.882.814.826-3.799-.234-.38A9.818 9.818 0 1112 21.818z"/></svg>
                    WhatsApp developer
                  </a>
                )}
              </div>
            </div>

            {/* Compliance documents */}
            {(() => {
              const docs = [
                { label: "Permit", provided: project.permitProvided, verified: project.permitVerified },
                { label: "Parent Deed", provided: project.parentDeedProvided, verified: project.parentDeedVerified },
                { label: "Compliance Certificate", provided: project.complianceCertProvided, verified: project.complianceCertVerified },
              ]
              if (!docs.some(x => x.provided)) return null
              const allVerified = docs.every(x => x.verified)
              return (
                <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: 18, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)" }}>Compliance documents</div>
                    {allVerified && <span style={{ fontSize: 11, fontWeight: 700, color: "#3d8b5f", background: "rgba(61,139,95,0.12)", padding: "2px 8px", borderRadius: 20 }}>Fully compliant</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.5 }}>What this developer has provided to Zim Estates for this development.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {docs.map(doc => (
                      <div key={doc.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>{doc.label}</span>
                        {doc.verified ? (
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#3d8b5f", display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ display: "inline-grid", placeItems: "center", width: 16, height: 16, borderRadius: "50%", background: "#3d8b5f", color: "white", fontSize: 9 }}>✓</span>
                            Verified
                          </span>
                        ) : doc.provided ? (
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gold-deep)" }}>Provided</span>
                        ) : (
                          <span style={{ fontSize: 12, color: "var(--muted)" }}>Not provided</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 14, lineHeight: 1.5 }}>&ldquo;Verified&rdquo; means a Zim Estates admin has sighted the document. Always confirm originals with your conveyancer before paying.</div>
                </div>
              )
            })()}

            {/* Payment calculator */}
            <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: 18, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Payment calculator</div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 22, lineHeight: 1.5 }}>Based on the lowest listed unit price</div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 9 }}>Deposit</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[10, 20, 25, 30, 50].map(p => (
                    <button key={p} onClick={() => setDepositPct(p)}
                      style={{ padding: "7px 13px", borderRadius: 8, border: "1.5px solid " + (depositPct === p ? "var(--navy)" : "var(--line)"), fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: depositPct === p ? "var(--navy)" : "white", color: depositPct === p ? "var(--cream)" : "var(--navy)", transition: "all 0.12s" }}>
                      {p}%
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 9 }}>Repayment period</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[12, 24, 36].map(m => (
                    <button key={m} onClick={() => setCalcMonths(m)}
                      style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1.5px solid " + (calcMonths === m ? "var(--navy)" : "var(--line)"), fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: calcMonths === m ? "var(--navy)" : "white", color: calcMonths === m ? "var(--cream)" : "var(--navy)", transition: "all 0.12s" }}>
                      {m} mo
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ background: "var(--cream-warm)", borderRadius: 13, padding: "17px 18px", marginBottom: 16, border: "1px solid var(--line)" }}>
                {[
                  ["Unit price (from)", fmt(calcPrice)],
                  ["Deposit " + depositPct + "%", fmt(depositAmt)],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 9 }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>{val}</span>
                  </div>
                ))}
                <div style={{ height: 1, background: "var(--line)", margin: "10px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>Monthly ({calcMonths} mo)</span>
                  <span style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: "var(--gold-deep)" }}>{fmt(monthly)}</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginBottom: 14 }}>Indicative only · subject to developer's payment terms</div>
              {d.whatsappNum && (
                <a href={`/api/go/whatsapp?project=${encodeURIComponent(project.id)}&topic=${d.whatsappPlan}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: "block", padding: "13px 0", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: "none", textAlign: "center" }}>
                  Enquire about this plan
                </a>
              )}
            </div>

            {/* Waitlist */}
            <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: 18, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              {waitlistDone ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(61,139,95,0.1)", display: "grid", placeItems: "center", margin: "0 auto 14px", fontSize: 24 }}>✓</div>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>You're on the waitlist</div>
                  <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.65 }}>We'll notify you when units open for {project.name}.</div>
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Join the waitlist</div>
                  <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20, lineHeight: 1.5 }}>Be first in line when units become available. No obligation.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                    <input value={waitlistForm.name} onChange={e => setWaitlistForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" style={inp} />
                    <input value={waitlistForm.email} onChange={e => setWaitlistForm(f => ({ ...f, email: e.target.value }))} placeholder="Email address" type="email" style={inp} />
                    <input value={waitlistForm.phone} onChange={e => setWaitlistForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone (+263…)" style={inp} />
                    {d.typeList.length > 0 && (
                      <select value={waitlistForm.unitType} onChange={e => setWaitlistForm(f => ({ ...f, unitType: e.target.value }))} style={inp}>
                        <option value="">Any unit type</option>
                        {d.typeList.map(tn => <option key={tn} value={tn}>{tn}</option>)}
                      </select>
                    )}
                    {waitlistError && (
                      <div style={{ fontSize: 13, color: "var(--red-text)", background: "rgba(196,68,68,0.06)", borderRadius: 8, padding: "9px 13px" }}>{waitlistError}</div>
                    )}
                    <button onClick={joinWaitlist} disabled={waitlistLoading}
                      style={{ width: "100%", padding: 13, background: waitlistLoading ? "var(--muted)" : "var(--gold)", color: waitlistLoading ? "var(--cream)" : "var(--navy)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: waitlistLoading ? "not-allowed" : "pointer", fontFamily: "inherit", letterSpacing: "0.01em" }}>
                      {waitlistLoading ? "Joining…" : "Join waitlist"}
                    </button>
                    <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center" }}>No spam · unsubscribe anytime</div>
                  </div>
                </>
              )}
            </div>

            {/* Subscribe to updates */}
            <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: 18, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              {subDone ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(200,150,62,0.12)", display: "grid", placeItems: "center", margin: "0 auto 14px", fontSize: 24 }}>📬</div>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>You're subscribed</div>
                  <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.65 }}>We'll email you every time the developer posts a progress update. Check your inbox for a confirmation.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 22 }}>🔔</span>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 600, color: "var(--navy)" }}>Get construction updates</div>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20, lineHeight: 1.5 }}>
                    Subscribe to receive an email whenever the developer posts progress on this project.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                    <input value={subName} onChange={e => setSubName(e.target.value)} placeholder="Your name (optional)" style={inp} />
                    <input value={subEmail} onChange={e => setSubEmail(e.target.value)} placeholder="Email address" type="email" style={inp} />
                    {subError && (
                      <div style={{ fontSize: 13, color: "var(--red-text)", background: "rgba(196,68,68,0.06)", borderRadius: 8, padding: "9px 13px" }}>{subError}</div>
                    )}
                    <button onClick={subscribeToUpdates} disabled={subLoading}
                      style={{ width: "100%", padding: 13, background: subLoading ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: subLoading ? "not-allowed" : "pointer", fontFamily: "inherit", letterSpacing: "0.01em" }}>
                      {subLoading ? "Subscribing…" : "Subscribe to updates"}
                    </button>
                    <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center" }}>Free · one-click unsubscribe anytime</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
